import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import CheckIcon from 'components/material-symbol/icons/Check'
import {
  createValidationSchema,
  validateCustomSlugAvailability,
} from 'components/coaches-profile/constants'
import { submissionHandler } from 'components/coaches-profile/utils'
import { gtmEvent } from 'lib/gtm'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import CreateProfileSectionNav, {
  getProfileSections,
  getActiveSectionIndex,
} from './create-profile-section-nav'
import AboutYouSection from './form-sections/about-you-section'
import ContactLocationSection from './form-sections/contact-location-section'
import PersonalMediaSection from './form-sections/personal-media-section'
import WebAddressSection from './form-sections/web-address-section'
import FacilitiesStep from '../steps/facilities'
import Experience from '../steps/experience'
import SstStep from '../steps/sst'
import SuccessStep from '../steps/success'
import { makeFormFieldObj } from '../v3Utils'

const CREATE_COACH_PROFILE = gql`
  mutation CoachProfileCreate($params: CoachProfileCreateInput!) {
    CoachProfileCreate(params: $params) {
      message
      success
    }
  }
`

const CreateCoachProfileForm = ({
  memberProfile,
  facilities = [],
  dialog,
  isOnboarding = false,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const includeFacilities = facilities.length === 0
  const sections = useMemo(() => getProfileSections({ includeFacilities }), [
    includeFacilities,
  ])

  const [createdCoachProfile, setCreatedCoachProfile] = useState(false)
  const [coachSlug, setCoachSlug] = useState(null)
  const [activeSection, setActiveSection] = useState(sections[0].id)
  const [completedSectionIds, setCompletedSectionIds] = useState(
    () => new Set(),
  )
  const completedSectionIdsRef = useRef(completedSectionIds)
  completedSectionIdsRef.current = completedSectionIds

  const activeStepIndex = getActiveSectionIndex(sections, activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === sections.length - 1
  const currentSection = sections[activeStepIndex]

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const [createCoachProfile, { loading }] = useMutation(CREATE_COACH_PROFILE, {
    onCompleted: data => {
      if (data.CoachProfileCreate.success) {
        setCreatedCoachProfile(true)
        gtmEvent({
          event: 'formSubmit',
          formCategory: 'profile',
          formAction: 'create-profile',
        })
      } else {
        const message = data.CoachProfileCreate.message || ''
        const friendlyMessage = message.includes(
          'already has a PGA.com Coach Profile',
        )
          ? 'You already have a coach profile. Go to Coach Profile to view or edit it.'
          : message

        enqueueSnackbar(friendlyMessage, {
          variant: 'error',
          autoHideDuration: 5000,
        })
      }
    },
    onError: error => {
      window.rg4js &&
        window.rg4js('send', {
          error: error,
          tags: ['create-coach-profile'],
        })
      enqueueSnackbar(error?.message || 'Unable to create profile.', {
        variant: 'error',
        autoHideDuration: 5000,
      })
    },
  })

  const memberDefaults = useMemo(() => makeFormFieldObj(memberProfile), [
    memberProfile,
  ])

  const defaultValues = useMemo(
    () => ({
      ...(memberDefaults ?? {}),
      facilities: memberDefaults?.facilities ?? [],
    }),
    [memberDefaults],
  )

  const form = useForm({
    defaultValues,
    shouldUnregister: false,
    resolver: yupResolver(createValidationSchema),
  })

  const { trigger, watch } = form

  const getStepFields = useCallback(section => section.fields || [], [])

  const getLiveValidationFields = useCallback(
    fields => fields.filter(field => field !== 'customSlug' && field !== 'bio'),
    [],
  )

  const currentSectionFields = useMemo(() => {
    return getLiveValidationFields(getStepFields(currentSection))
  }, [currentSection, getLiveValidationFields, getStepFields])

  const watchedSectionValues = watch(currentSectionFields)

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (currentSectionFields.length) {
        await trigger(currentSectionFields, false)
      }
    }, 300)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [currentSectionFields, trigger, watchedSectionValues, activeSection])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const markSectionComplete = useCallback(sectionId => {
    setCompletedSectionIds(prev => {
      if (prev.has(sectionId)) {
        return prev
      }

      const next = new Set(prev)
      next.add(sectionId)
      return next
    })
  }, [])

  useEffect(() => {
    if (completedSectionIdsRef.current.size === 0) {
      return undefined
    }

    let cancelled = false
    const timeoutId = setTimeout(async () => {
      const completed = completedSectionIdsRef.current
      const next = new Set()

      for (const section of sections) {
        if (!completed.has(section.id)) {
          continue
        }

        const fields = getLiveValidationFields(section.fields || [])
        const isValid = fields.length ? await trigger(fields, false) : true

        if (!cancelled && isValid) {
          next.add(section.id)
        }
      }

      if (!cancelled) {
        setCompletedSectionIds(prev => {
          if (
            prev.size === next.size &&
            [...prev].every(sectionId => next.has(sectionId))
          ) {
            return prev
          }

          return next
        })
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [getLiveValidationFields, sections, trigger, watchedSectionValues])

  const handleCancel = useCallback(() => {
    history.push(isOnboarding ? '/pga-coach' : '/pga-coach/profile')
  }, [history, isOnboarding])

  const navigateToSectionWithError = useCallback(
    formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = sections.find(section => {
        const fields = getStepFields(section)
        return fields.some(field => errorFields.includes(field))
      })

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    },
    [getStepFields, scrollToFormTop, sections],
  )

  const showFirstValidationError = useCallback(
    formErrors => {
      const errorMessage = getFirstErrorMessage(formErrors)

      if (errorMessage) {
        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(sections[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop, sections])

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid && fields.includes('customSlug')) {
      const availabilityError = await validateCustomSlugAvailability(
        form.getValues('customSlug'),
      )

      if (availabilityError) {
        form.setError('customSlug', {
          type: 'manual',
          message: availabilityError,
        })
        enqueueSnackbar(availabilityError, { variant: 'error' })
        return
      }
    }

    if (isValid) {
      markSectionComplete(currentSection.id)
      setActiveSection(sections[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const formErrors = form.formState.errors
    navigateToSectionWithError(formErrors)
    showFirstValidationError(formErrors)
  }, [
    activeStepIndex,
    currentSection,
    enqueueSnackbar,
    form,
    getStepFields,
    isLastStep,
    markSectionComplete,
    navigateToSectionWithError,
    scrollToFormTop,
    sections,
    showFirstValidationError,
    trigger,
  ])

  const onSubmit = async formValues => {
    const availabilityError = await validateCustomSlugAvailability(
      formValues.customSlug,
    )

    if (availabilityError) {
      form.setError('customSlug', {
        type: 'manual',
        message: availabilityError,
      })
      setActiveSection('profile-section-web-address')
      scrollToFormTop()
      enqueueSnackbar(availabilityError, { variant: 'error' })
      return
    }

    setCoachSlug(formValues.customSlug)
    submissionHandler(
      formValues,
      true,
      createCoachProfile,
      undefined,
      () => {},
      () => {},
    )
  }

  const handleCreateClick = e => {
    if (!isLastStep) {
      e.preventDefault()
      return handleNext()
    }

    return form.handleSubmit(onSubmit, formErrors => {
      console.log('formErrors', formErrors)
      console.log('form.getValues()', form.getValues())
      navigateToSectionWithError(formErrors)
      showFirstValidationError(formErrors)
    })(e)
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile-section-about':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <AboutYouSection memberProfile={memberProfile} />
          </FormSectionContainer>
        )
      case 'profile-section-contact':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <ContactLocationSection />
          </FormSectionContainer>
        )
      case 'profile-section-personal':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <PersonalMediaSection />
          </FormSectionContainer>
        )
      case 'profile-section-web-address':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <WebAddressSection />
          </FormSectionContainer>
        )
      case 'profile-section-facilities':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <FacilitiesStep hideTitle />
          </FormSectionContainer>
        )
      case 'profile-section-experience':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <Experience hideTitle />
          </FormSectionContainer>
        )
      case 'profile-section-services':
        return (
          <FormSectionContainer
            id={currentSection.id}
            title={currentSection.pageTitle}
          >
            <SstStep hideTitle />
          </FormSectionContainer>
        )
      default:
        return null
    }
  }

  if (createdCoachProfile) {
    return <SuccessStep slug={coachSlug} dialog={dialog} />
  }

  return (
    <Box>
      <FormProvider {...form}>
        <form onSubmit={handleCreateClick}>
          <Box className={classes.actionBar}>
            <Button
              type="button"
              className={classes.cancelAction}
              startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.saveAction}
              disabled={!isLastStep || loading}
              startIcon={<CheckIcon fontSize="small" />}
            >
              Create Profile
            </Button>
          </Box>

          {loading ? (
            <LinearProgress color="primary" className={classes.loadingStyles} />
          ) : null}
          <Container maxWidth="lg">
            <Box pt={4} pb={3}>
              <Box className={classes.layoutGrid}>
                <Box className={classes.navColumn}>
                  <CreateProfileSectionNav
                    sections={sections}
                    activeSection={activeSection}
                    completedSectionIds={completedSectionIds}
                    onSectionClick={handleSectionClick}
                  />
                </Box>

                <Box className={classes.formColumn}>
                  {renderActiveSection()}
                  <EditEventPagination
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                </Box>
              </Box>
            </Box>
          </Container>
        </form>
      </FormProvider>
    </Box>
  )
}

export default CreateCoachProfileForm
